import React, { useMemo, useState } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useLocation, useNavigate } from "react-router-dom";
import { keyframes } from "@emotion/react";

import image1 from "../images/Recurso.svg";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Navbar() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);

  const goHomeAndScroll = (id) => {
    if (location.pathname === "/") {
      scrollToId(id);
      return;
    }
    navigate("/");
    setTimeout(() => scrollToId(id), 80);
  };

  // Link "delicado"
  const linkSx = {
    fontSize: { xs: "0.95rem", sm: "1rem" },
    fontWeight: 500,
    textTransform: "none",
    letterSpacing: 0,
    color: "inherit",
    px: 0,
    py: 0.25,
    minWidth: "auto",
    borderRadius: 0,
    backgroundColor: "transparent",
    position: "relative",
    transition: "transform 220ms ease, opacity 220ms ease",
    "&:hover": {
      backgroundColor: "transparent",
      transform: "translateY(-1px)",
    },
    "&::after": {
      content: '""',
      position: "absolute",
      left: 0,
      right: 0,
      bottom: -6,
      height: 2,
      backgroundColor: "currentColor",
      transform: "scaleX(0)",
      transformOrigin: "left",
      transition: "transform 240ms ease",
      opacity: 0.85,
    },
    "&:hover::after": {
      transform: "scaleX(1)",
    },
  };

  // ✅ EXACTO como Hero1 (mismo keyframes)
  const gradientShift = useMemo(
    () => keyframes`
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    `,
    [],
  );

  // ✅ EXACTO como Hero1 (mismo sx)
  const reserveSx = useMemo(
    () => ({
      fontSize: { xs: "1.02rem", sm: "1.08rem", md: "1.1rem" },
      borderRadius: 2,
      px: { xs: 3, sm: 3.6 },
      py: { xs: 1.35, sm: 1.5 },
      fontWeight: 600,
      textTransform: "none",
      color: "#fff",
      boxShadow: "0 10px 24px rgba(0,0,0,0.22)",
      background: "linear-gradient(270deg, #2DB193, #16a357, #c72e64)",
      backgroundSize: "600% 600%",
      animation: `${gradientShift} 3.2s ease infinite`,
      transition: "transform 220ms ease, box-shadow 220ms ease, filter 220ms ease",
      "&:hover": {
        transform: "translateY(-1px)",
        boxShadow: "0 14px 34px rgba(0,0,0,0.28)",
        filter: "brightness(1.05)",
      },
    }),
    [gradientShift],
  );

  return (
    <Box sx={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1201 }}>
      <AppBar position="static" color="primary" elevation={0}>
        <Toolbar
          disableGutters
          sx={{
            height: isMobile ? 70 : 80,
            minHeight: "unset",
            px: { xs: 2, sm: 3, md: 4 },
            py: isMobile ? 1 : 1.5,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 1000,
              mx: "auto",
              display: "flex",
              alignItems: "center",
              position: "relative",
              justifyContent: { xs: "center", sm: "space-between" },
              px: { xs: 0.5, sm: 1.5, md: 2.5 },
              gap: { xs: 1, sm: 2 },
            }}
          >
            {/* Logo */}
            <Box
              onClick={() => navigate("/")}
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                userSelect: "none",
                mr: { xs: 0, sm: 1 },
              }}
            >
              <img
                src={image1}
                alt="Logo"
                style={{
                  height: isMobile ? 60 : 70,
                  objectFit: "contain",
                }}
              />
            </Box>

            {/* Desktop nav */}
            {!isMobile ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                <Button sx={linkSx} onClick={() => goHomeAndScroll("instalaciones")}>
                  Instalaciones
                </Button>

                <Button sx={linkSx} onClick={() => goHomeAndScroll("ubicacion")}>
                  Ubicación
                </Button>

                <Button sx={linkSx} onClick={() => goHomeAndScroll("experiencias")}>
                  Experiencias
                </Button>

                <Button sx={linkSx} onClick={() => goHomeAndScroll("contactanos")}>
                  Contáctanos
                </Button>

                <Button
                  variant="contained"
                  sx={reserveSx}
                  onClick={() => navigate("/reserve")}
                >
                  Reservar ahora
                </Button>
              </Box>
            ) : (
              <>
                <IconButton
                  onClick={() => setOpen(true)}
                  sx={{
                    color: "inherit",
                    position: { xs: "absolute", sm: "static" },
                    right: { xs: 0, sm: "auto" },
                  }}
                  aria-label="Abrir menú"
                >
                  <MenuIcon />
                </IconButton>

                <Drawer
                  anchor="right"
                  open={open}
                  onClose={() => setOpen(false)}
                  PaperProps={{
                    sx: {
                      backgroundColor: "#E9E4D6",
                      color: "#1b1b1b",
                    },
                  }}
                >
                  <Box sx={{ width: 280, p: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                      <img src={image1} alt="Logo" style={{ height: 34 }} />
                    </Box>

                    <Divider sx={{ mb: 1.5 }} />

                    <List sx={{ p: 0 }}>
                      <ListItemButton
                        onClick={() => {
                          setOpen(false);
                          goHomeAndScroll("instalaciones");
                        }}
                      >
                        <ListItemText primary="Instalaciones" />
                      </ListItemButton>

                      <ListItemButton
                        onClick={() => {
                          setOpen(false);
                          goHomeAndScroll("ubicacion");
                        }}
                      >
                        <ListItemText primary="Ubicación" />
                      </ListItemButton>

                      <ListItemButton
                        onClick={() => {
                          setOpen(false);
                          goHomeAndScroll("experiencias");
                        }}
                      >
                        <ListItemText primary="Experiencias" />
                      </ListItemButton>

                      <ListItemButton
                        onClick={() => {
                          setOpen(false);
                          goHomeAndScroll("contactanos");
                        }}
                      >
                        <ListItemText primary="Contáctanos" />
                      </ListItemButton>

                      <Box sx={{ mt: 1.5 }}>
                        <Button
                          fullWidth
                          variant="contained"
                          sx={reserveSx}
                          onClick={() => {
                            setOpen(false);
                            navigate("/reserve");
                          }}
                        >
                          Reservar ahora
                        </Button>
                      </Box>
                    </List>
                  </Box>
                </Drawer>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>
    </Box>
  );
}
